import { supabaseClient } from "@/lib/supabase";
import { DEFAULT_LOCALE, type SupportedLocale } from "@patient-watch/i18n";

const IN_CHUNK = 100;

async function fetchInChunks<T>(ids: string[], query: (chunk: string[]) => Promise<T[]>): Promise<T[]> {
    const unique = [...new Set(ids.filter(Boolean))];
    if (unique.length === 0) return [];
    const chunks: string[][] = [];
    for (let i = 0; i < unique.length; i += IN_CHUNK) {
        chunks.push(unique.slice(i, i + IN_CHUNK));
    }
    const rows = await Promise.all(chunks.map((chunk) => query(chunk)));
    return rows.flat();
}

/** form_id → translated form_name */
export async function fetchFormNameTranslations(
    formIds: string[],
    locale: SupportedLocale
): Promise<Map<string, string>> {
    if (locale === DEFAULT_LOCALE) return new Map();
    const rows = await fetchInChunks(formIds, async (chunk) => {
        const { data, error } = await supabaseClient
            .from("form_translations")
            .select("form_id, form_name")
            .eq("locale", locale)
            .in("form_id", chunk);
        if (error) throw error;
        return data ?? [];
    });
    const map = new Map<string, string>();
    for (const row of rows) {
        if (row.form_id && row.form_name) map.set(row.form_id, row.form_name);
    }
    return map;
}

/** input_id → translated input_label */
export async function fetchInputLabelTranslations(
    inputIds: string[],
    locale: SupportedLocale
): Promise<Map<string, string>> {
    if (locale === DEFAULT_LOCALE) return new Map();
    const rows = await fetchInChunks(inputIds, async (chunk) => {
        const { data, error } = await supabaseClient
            .from("input_translations")
            .select("input_id, input_label")
            .eq("locale", locale)
            .in("input_id", chunk);
        if (error) throw error;
        return data ?? [];
    });
    const map = new Map<string, string>();
    for (const row of rows) {
        if (row.input_id && row.input_label) map.set(row.input_id, row.input_label);
    }
    return map;
}
